import React, { createContext, useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { createLead, createBooking, getAvailability } from "../../lib/api";

/* ---------- Buttons ---------- */
export const GoldButton = ({ children, className = "", testid, ...props }) => (
  <button
    data-testid={testid}
    className={`group relative inline-flex items-center gap-2 rounded-full bg-[#d4af37] px-7 py-3.5 font-body text-sm font-semibold text-[#050505] transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#f3e5ab] hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] active:scale-95 ${className}`}
    {...props}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

export const GhostButton = ({ children, className = "", testid, ...props }) => (
  <button
    data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-white/[0.02] px-7 py-3.5 font-body text-sm font-semibold text-[#f5f5f5] backdrop-blur-md transition-[background-color,border-color] duration-300 hover:border-[#d4af37] hover:bg-[#d4af37]/10 ${className}`}
    {...props}
  >
    {children}
  </button>
);

/* ---------- Reveal wrapper ---------- */
export const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children, testid }) => (
  <span data-testid={testid} className="overline">
    {children}
  </span>
);

/* ---------- Lead / Booking dialog context ---------- */
const DialogCtx = createContext(null);
export const useDialog = () => useContext(DialogCtx);

const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-body text-sm text-[#f5f5f5] placeholder-[#666] outline-none transition-colors duration-300 focus:border-[#d4af37] focus:shadow-[0_0_0_1px_rgba(212,175,55,0.4)]";

export const DialogProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, mode: "lead", interest: "", title: "" });
  const openLead = (interest = "", title = "Start your journey") =>
    setState({ open: true, mode: "lead", interest, title });
  const openBooking = (interest = "Free Consultation", title = "Book a free consultation") =>
    setState({ open: true, mode: "booking", interest, title });
  const close = () => setState((s) => ({ ...s, open: false }));

  return (
    <DialogCtx.Provider value={{ openLead, openBooking }}>
      {children}
      <AnimatePresence>
        {state.open && <FormDialog state={state} close={close} />}
      </AnimatePresence>
    </DialogCtx.Provider>
  );
};

const FormDialog = ({ state, close }) => {
  const isBooking = state.mode === "booking";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    service: state.interest || "Credit Repair", preferred_date: "", preferred_time: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!isBooking || !form.preferred_date) { setSlots([]); return; }
    setSlotLoading(true);
    getAvailability(form.preferred_date)
      .then((d) => setSlots(d.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotLoading(false));
  }, [form.preferred_date, isBooking]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isBooking) {
        await createBooking({
          name: form.name, email: form.email, phone: form.phone,
          service: form.service, preferred_date: form.preferred_date,
          preferred_time: form.preferred_time, notes: form.notes,
        });
      } else {
        await createLead({
          name: form.name, email: form.email, phone: form.phone,
          message: form.message, interest: state.interest, path: state.interest,
        });
      }
      setDone(true);
      toast.success("Received — the BGM team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="form-dialog"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <motion.div
        initial={{ scale: 0.94, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 20, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="glass-solid gold-border-glow relative z-10 w-full max-w-lg rounded-3xl p-8"
      >
        <button data-testid="dialog-close" onClick={close}
          className="absolute right-5 top-5 text-[#888] transition-colors hover:text-[#d4af37]">
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="py-8 text-center" data-testid="dialog-success">
            <CheckCircle2 className="mx-auto mb-5 h-14 w-14 text-[#d4af37]" />
            <h3 className="font-display text-2xl text-[#f5f5f5]">You're in.</h3>
            <p className="mt-3 font-body text-sm text-[#a3a3a3]">
              A confirmation is on its way. A BGM strategist will connect with you soon.
            </p>
            <GoldButton testid="dialog-success-close" className="mx-auto mt-7" onClick={close}>
              Continue exploring
            </GoldButton>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
            <Overline>{isBooking ? "Free Consultation" : "Begin"}</Overline>
            <h3 className="font-display text-2xl tracking-tighter-x text-[#f5f5f5]">{state.title}</h3>
            {state.interest && !isBooking && (
              <p className="font-body text-xs text-[#a3a3a3]">Interested in: <span className="text-gold">{state.interest}</span></p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="field-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="field-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <input data-testid="field-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
            {isBooking ? (
              <>
                <select data-testid="field-service" value={form.service} onChange={set("service")} className={inputCls}>
                  {["Credit Repair", "Credit Education", "Business Funding Education", "Digital Products", "Financial Coaching"].map((s) => (
                    <option key={s} value={s} className="bg-[#0f0f10]">{s}</option>
                  ))}
                </select>
                <input data-testid="field-date" required type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value, preferred_time: "" })} className={inputCls} />
                {form.preferred_date && (
                  <div data-testid="slot-picker">
                    <p className="mb-2 font-body text-xs text-[#888]">Available times (Mon–Fri, 15 min)</p>
                    {slotLoading ? (
                      <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-gold" /></div>
                    ) : slots.length === 0 ? (
                      <p className="font-body text-xs text-[#a3a3a3]">No slots for this date. Try another weekday.</p>
                    ) : (
                      <div className="grid max-h-36 grid-cols-4 gap-2 overflow-y-auto pr-1">
                        {slots.map((t) => (
                          <button type="button" key={t} data-testid={`slot-${t}`} onClick={() => setForm({ ...form, preferred_time: t })}
                            className={`rounded-lg border px-2 py-1.5 font-body text-xs transition-colors ${form.preferred_time === t ? "border-[#d4af37] bg-[#d4af37] text-[#050505]" : "border-white/10 text-[#c9c9c9] hover:border-[#d4af37]/50"}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <textarea data-testid="field-notes" rows={3} placeholder="Anything we should know? (optional)" value={form.notes} onChange={set("notes")} className={inputCls} />
              </>
            ) : (
              <textarea data-testid="field-message" rows={3} placeholder="Tell us your goal (optional)" value={form.message} onChange={set("message")} className={inputCls} />
            )}
            <GoldButton testid="form-submit" type="submit" className="w-full justify-center" disabled={loading || (isBooking && !form.preferred_time)}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isBooking ? "Request consultation" : "Send my request"}
            </GoldButton>
            <p className="text-center font-body text-[10px] leading-relaxed text-[#666]">
              BGM provides credit education & financial empowerment resources. Results vary; no specific outcomes guaranteed.
            </p>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};
